import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Divider, RadioInput, Typography } from '@guildeducationinc/recess';
import { MacroPicker } from '@src/components/MacroPicker/MacroPicker';
import { DirtyContext } from '@src/contexts/DirtyContextProvider';
import { UPDATE_PAYMENT_REQUEST_STATUS } from '@src/graphql/queries/paymentRequest';
import { useAuth } from '@src/hooks/useAuth';
import { PaymentMethod, PaymentRequestStatus, PreimbursementStatus, } from '@src/types';
import { prepareForSave } from '@src/utilities/gqlHelper';
import { fillPlaceholders } from '@src/utilities/paymentRequestHelper';
import { GlobalPermission } from '@src/utilities/permissions';
import { omit } from 'lodash';
import { HtmlEditor, Messages, MessageType, SAVE_COMMENT, TrackableButton, useAlert, useLocale, useSessionState, } from 'pe-core-frontend';
import { useContext, useState } from 'react';
import styles from './PaymentRequestActions.module.scss';
var Actions;
(function (Actions) {
    Actions["ADD_INTERNAL_NOTE"] = "ADD_INTERNAL_NOTE";
    Actions["APPROVE"] = "APPROVE";
    Actions["CLOSE"] = "CLOSE";
    Actions["INFO_REQUESTED"] = "INFO_REQUESTED";
    Actions["REJECT"] = "REJECT";
    Actions["ON_HOLD"] = "ON_HOLD";
    Actions["EXPIRE"] = "EXPIRE";
    Actions["ADD_PUBLIC_COMMENT"] = "ADD_PUBLIC_COMMENT";
})(Actions || (Actions = {}));
/**
 * Component for handling actions related to a payment request.
 *
 * @component
 * @param {PaymentRequestActionsProps} props - The properties for the component.
 * @param {PaymentRequest} props.paymentRequest - The payment request object.
 * @param {Function} props.onSubmit - Callback function to be called after successful form submission.
 * @param {Function} props.onApprove - Callback function to be called when the payment request is approved.
 *
 * @returns {JSX.Element | null} The rendered component or null if the user does not have permission to approve or close the payment request.
 *
 * @example
 * <PaymentRequestActions
 *   paymentRequest={paymentRequest}
 *   onSubmit={handleSubmit}
 *   onApprove={handleApprove}
 * />
 *
 * @remarks
 * This component uses several hooks and context:
 * - `useLocale` for localization.
 * - `useAuth` for authentication and permission checks.
 * - `DirtyContext` to manage the dirty state of the form.
 * - `useSessionState` to manage session state for action and content.
 * - `useMutation` for GraphQL mutations to update payment request status and save comments.
 *
 * The component conditionally renders different sets of radio inputs based on the user's permissions and the current status of the payment request.
 * It also includes a macro picker and an HTML editor for adding comments.
 * The component handles form submission and updates the payment request status or saves comments based on the selected action.
 */
export const PaymentRequestActions = ({ paymentRequest, onSubmit, onApprove, }) => {
    const { t, formatToLocalDate } = useLocale();
    const { hasPermission, currentUser } = useAuth();
    const { setIsDirty } = useContext(DirtyContext);
    const { showAlert } = useAlert();
    const [action, setAction] = useSessionState(`pmtReqAction-${paymentRequest.id}`, null);
    const [content, setContent] = useSessionState(`pmtReqComment-${paymentRequest.id}`, '');
    const [isSaving, setIsSaving] = useState(false);
    const [messages, setMessages] = useState([]);
    const [updateStatus] = useMutation(UPDATE_PAYMENT_REQUEST_STATUS);
    const [saveComment] = useMutation(SAVE_COMMENT);
    const inProcessByCurrentUser = paymentRequest?.inProcess && paymentRequest?.processedById === currentUser?.id;
    const canApproveClaim = hasPermission(GlobalPermission.PROCESS_CLAIMS) &&
        paymentRequest &&
        [PaymentRequestStatus.DOCUMENT_REVIEW, PaymentRequestStatus.ON_HOLD].includes(paymentRequest.status) &&
        inProcessByCurrentUser;
    const canCloseInfoNeededClaim = hasPermission(GlobalPermission.PROCESS_CLAIMS) &&
        paymentRequest &&
        paymentRequest.status === PaymentRequestStatus.INFO_REQUESTED;
    const canOnlyComment = hasPermission(GlobalPermission.VIEW_CLAIMS) &&
        paymentRequest &&
        !canApproveClaim &&
        !canCloseInfoNeededClaim;
    const handleMacroSelect = (macro) => {
        macro &&
            setContent(`${content}${fillPlaceholders(macro.content, paymentRequest, formatToLocalDate)}`);
    };
    const handleUpdateStatus = (status) => async (content) => {
        setIsSaving(true);
        setMessages([]);
        let success = false;
        try {
            if (paymentRequest && paymentRequest.id > 0) {
                const result = await updateStatus({
                    variables: {
                        id: paymentRequest.id,
                        status,
                        commentContent: content,
                    },
                });
                if (result.data?.updatePaymentRequestStatus?.messages) {
                    setMessages(result.data.updatePaymentRequestStatus.messages);
                }
                else {
                    setIsDirty(false);
                    success = true;
                }
            }
        }
        catch (e) {
            console.error('e', e);
            setMessages([
                {
                    value: t('Error saving payment request. Please try again.'),
                    type: MessageType.ERROR,
                    params: {},
                },
            ]);
        }
        finally {
            setIsSaving(false);
        }
        return success;
    };
    const handleSubmit = async () => {
        let status = null;
        let isInternal = false;
        let isCommentRequired = true;
        switch (action) {
            case Actions.CLOSE:
                status = PaymentRequestStatus.CLOSED;
                break;
            case Actions.REJECT:
                status = PaymentRequestStatus.REJECTED;
                break;
            case Actions.INFO_REQUESTED:
                status = PaymentRequestStatus.INFO_REQUESTED;
                break;
            case Actions.APPROVE:
                status = PaymentRequestStatus.APPROVED;
                isCommentRequired = false;
                onApprove(content);
                return;
            case Actions.ON_HOLD:
                status = PaymentRequestStatus.ON_HOLD;
                break;
            case Actions.EXPIRE:
                status = PaymentRequestStatus.EXPIRED;
                break;
            case Actions.ADD_INTERNAL_NOTE:
                isInternal = true;
                break;
            case Actions.ADD_PUBLIC_COMMENT:
                isInternal = false;
                break;
        }
        if (isCommentRequired && content.trim() === '') {
            setMessages([{ value: t('Comment is required'), type: MessageType.ERROR }]);
            return;
        }
        let success = false;
        if (content && !status) {
            // save the comment
            const comment = {
                id: 0,
                message: content,
                isInternal,
                commentableId: paymentRequest.id,
                commentableType: 'payment_requests',
                createdById: currentUser?.id || 0,
                updatedById: currentUser?.id || 0,
                clientId: paymentRequest.clientId,
            };
            const result = await saveComment({
                variables: {
                    input: omit(prepareForSave(comment), 'createdById', 'updatedById', 'createdBy', 'updatedBy'),
                },
            });
            if (result.errors) {
                setMessages(result.errors.map(e => ({ value: e.message, type: MessageType.ERROR })));
            }
            else if (result.data?.saveComment) {
                comment.id = result.data.saveComment.id;
                success = true;
                showAlert(t('Note added successfully'), 'success');
            }
        }
        else if (status && content) {
            // update the status if needed
            success = await handleUpdateStatus(status)(content);
        }
        if (success) {
            onSubmit();
            // once the status is updated, clear the form
            handleClear();
        }
    };
    const handleClear = () => {
        setAction(null);
        setContent('');
        setMessages([]);
    };
    if (!canOnlyComment && !canApproveClaim && !canCloseInfoNeededClaim) {
        return null;
    }
    return (_jsxs("div", { className: styles.container, children: [_jsx(Typography, { variant: 'title2', children: t('Actions') }), _jsx(Messages, { messages: messages }), _jsxs("div", { children: [canOnlyComment && (_jsxs("div", { className: 'flex gap-2', children: [_jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.ADD_INTERNAL_NOTE, checked: action === Actions.ADD_INTERNAL_NOTE, onChange: e => setAction(e.target.value), label: t('Add Internal Note') }), _jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.ADD_PUBLIC_COMMENT, checked: action === Actions.ADD_PUBLIC_COMMENT, onChange: e => setAction(e.target.value), label: t('Add Public Comment') })] })), canCloseInfoNeededClaim && (_jsxs("div", { className: 'flex gap-2', children: [_jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.CLOSE, checked: action === Actions.CLOSE, onChange: e => setAction(e.target.value), label: t('Cancel This Request') }), _jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.ADD_INTERNAL_NOTE, checked: action === Actions.ADD_INTERNAL_NOTE, onChange: e => setAction(e.target.value), label: t('Add Note') }), paymentRequest.paymentMethod === PaymentMethod.PREIMBURSEMENT &&
                                paymentRequest.preimbursementStatus === PreimbursementStatus.PENDING_PROOF && (_jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.EXPIRE, checked: action === Actions.EXPIRE, onChange: e => setAction(e.target.value), label: t('Expire') }))] })), canApproveClaim && (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'flex gap-2', children: [_jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.REJECT, checked: action === Actions.REJECT, onChange: e => setAction(e.target.value), label: t('Reject') }), _jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.INFO_REQUESTED, checked: action === Actions.INFO_REQUESTED, onChange: e => setAction(e.target.value), label: t('Request info') }), paymentRequest.status === PaymentRequestStatus.DOCUMENT_REVIEW && (_jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.ON_HOLD, checked: action === Actions.ON_HOLD, onChange: e => setAction(e.target.value), label: t('On hold') }))] }), _jsxs("div", { className: 'flex gap-2', children: [_jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.ADD_INTERNAL_NOTE, checked: action === Actions.ADD_INTERNAL_NOTE, onChange: e => setAction(e.target.value), label: t('Add Note') }), _jsx(RadioInput, { name: 'action', radioGroup: 'action', value: Actions.APPROVE, checked: action === Actions.APPROVE, onChange: e => setAction(e.target.value), label: t('Approve') })] })] }))] }), action && (_jsxs(_Fragment, { children: [!canOnlyComment && (_jsx(MacroPicker, { clientId: paymentRequest.clientId, onSelect: handleMacroSelect })), _jsx(HtmlEditor, { label: `${t('Comment')}${action !== Actions.APPROVE ? ' *' : ''}`, value: content, onChange: val => setContent(val), minimal: true })] })), _jsx(Divider, { opacity: '2' }), _jsxs("div", { className: 'flex gap-3', children: [_jsx(TrackableButton, { enText: 'Submit', tag: 'process_claim', buttonSize: 'small', appearance: 'primary', onClick: handleSubmit, disabled: isSaving || !action, children: t('Submit') }), _jsx(TrackableButton, { enText: 'Clear', tag: 'process_claim', buttonSize: 'small', appearance: 'outline', onClick: handleClear, disabled: isSaving || !action, children: t('Clear') })] })] }));
};
